import React, { useState } from "react";
import { api } from "../App";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Ruler, Calculator } from "lucide-react";
import { toast } from "sonner";
import FormattedNumberInput from "./FormattedNumberInput";
import CalcTablePanel from "./CalcTablePanel";
import {
  CATEGORIES,
  splitCategory,
  joinCategory,
  getSubNames,
  getSubItems,
} from "../data/categories";
import { getCalculator } from "../data/calculators";
import { trData } from "../data/dataI18n";
import { formatNumber, formatMoney } from "../utils/formatCurrency";
import { getTranslation } from "../i18n/translations";

// Alan hesabı: En×Boy (zemin), En×Yükseklik (tek duvar), 2×(En+Boy)×Yükseklik (oda duvarları)
const computeArea = (w, l, h) => {
  if (w > 0 && l > 0 && h > 0)
    return { area: 2 * (w + l) * h, labelKey: "wallAreaFormula" };
  if (w > 0 && h > 0) return { area: w * h, labelKey: "singleWallFormula" };
  if (w > 0 && l > 0) return { area: w * l, labelKey: "floorAreaFormula" };
  return null;
};

/**
 * 3-seviyeli kategori dropdown (Ana > Alt > Detay), ölçü ile otomatik m² hesabı ve
 * çoklu AI ile piyasa fiyat tahmini. Malzeme ve İşçilik fiyatı ayrı gösterilir.
 */
const ItemSmartFields = ({ item, index, itemType, lang, onPatch }) => {
  const t = (key) => getTranslation(lang, key);
  const { main, sub, item: detail } = splitCategory(item.category);
  const mainCat = CATEGORIES.find((c) => c.name === main);
  const subNames = getSubNames(mainCat);
  const subItems = getSubItems(mainCat, sub);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Kategoriye özel akıllı hesaplama tablosu (varsa)
  const calcDef = getCalculator(main, sub);
  const [showCalc, setShowCalc] = useState(
    () => !!(item.calc_values && Object.keys(item.calc_values).length > 0)
  );

  const w = parseFloat(item.width_m) || 0;
  const l = parseFloat(item.length_m) || 0;
  const h = parseFloat(item.height_m) || 0;
  const calc = computeArea(w, l, h);

  const handleMainCat = (name) => {
    const patch = { category: name };
    if (!item.name) patch.name = trData(lang, name);
    onPatch(patch);
  };

  const handleSubCat = (s) => {
    const patch = { category: joinCategory(main, s, "") };
    const localizedSubs = subNames.map((n) => trData(lang, n));
    if (
      !item.name ||
      item.name === main ||
      item.name === trData(lang, main) ||
      subNames.includes(item.name) ||
      localizedSubs.includes(item.name)
    ) {
      patch.name = trData(lang, s);
    }
    onPatch(patch);
  };

  const handleDetail = (d) => {
    const patch = { category: joinCategory(main, sub, d) };
    // Detay seçildiğinde kalem adını otomatik güncelle: "Alt - Detay"
    patch.name = `${trData(lang, sub)} - ${trData(lang, d)}`;
    onPatch(patch);
  };

  const handleDim = (field, value) => {
    const dims = { width_m: w, length_m: l, height_m: h, [field]: value };
    const result = computeArea(
      parseFloat(dims.width_m) || 0,
      parseFloat(dims.length_m) || 0,
      parseFloat(dims.height_m) || 0
    );
    const patch = { [field]: value };
    if (result) {
      patch.quantity = Math.round(result.area * 100) / 100;
      patch.unit = "m²";
    }
    onPatch(patch);
  };

  const fetchAiPrice = async () => {
    const name = item.name || detail || sub || main;
    if (!name) {
      toast.error(t('enterItemFirst'));
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await api.post("/price-estimate", {
        item_name: name,
        category: item.category || null,
        description: item.description || null,
        unit: item.unit || "m²",
        item_type: itemType,
      });
      setAiResult(res.data);
    } catch (error) {
      toast.error(t('aiPriceFailed'));
    } finally {
      setAiLoading(false);
    }
  };

  // Tahmini uygula: kullanıcı malzeme veya işçilik fiyatını birim fiyat alanına atayabilir
  const applyPrice = (value) => {
    if (!value || value <= 0) {
      toast.error(t('noPriceToApply'));
      return;
    }
    onPatch({ unit_price: value });
    toast.success(t('priceApplied'));
  };

  return (
    <div
      className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3"
      data-testid={`${itemType}-smart-fields-${index}`}
    >
      {/* 3-Seviyeli Kategori Dropdownları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-sm">{t('categoryLabel')}</Label>
          <Select value={mainCat ? main : ""} onValueChange={handleMainCat}>
            <SelectTrigger
              className="rounded-lg bg-white"
              data-testid={`${itemType}-category-select-${index}`}
            >
              <SelectValue
                placeholder={item.category && !mainCat ? item.category : t('selectCategoryPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {trData(lang, c.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {mainCat && subNames.length > 0 && (
          <div>
            <Label className="text-sm">{t('subCategoryLabel')}</Label>
            <Select value={sub || ""} onValueChange={handleSubCat}>
              <SelectTrigger
                className="rounded-lg bg-white"
                data-testid={`${itemType}-subcategory-select-${index}`}
              >
                <SelectValue placeholder={t('selectSubCategoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {subNames.map((s) => (
                  <SelectItem key={s} value={s}>
                    {trData(lang, s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {mainCat && sub && subItems.length > 0 && (
          <div>
            <Label className="text-sm">{t('detailLabel')}</Label>
            <Select value={detail || ""} onValueChange={handleDetail}>
              <SelectTrigger
                className="rounded-lg bg-white"
                data-testid={`${itemType}-detail-select-${index}`}
              >
                <SelectValue placeholder={t('selectDetailPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {subItems.map((d) => (
                  <SelectItem key={d} value={d}>
                    {trData(lang, d)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Kategoriye özel akıllı hesaplama tablosu */}
      {calcDef && (
        <div>
          <Button
            type="button"
            variant={showCalc ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowCalc(!showCalc)}
            className="rounded-lg border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid={`${itemType}-calc-toggle-${index}`}
          >
            <Calculator className="w-4 h-4 mr-1.5" />
            {showCalc ? trData(lang, "Hesaplama Tablosunu Gizle") : trData(lang, calcDef.title)}
          </Button>
          {showCalc && (
            <div className="mt-2">
              <CalcTablePanel
                item={item}
                itemType={itemType}
                index={index}
                calcDef={calcDef}
                onPatch={onPatch}
                lang={lang}
              />
            </div>
          )}
        </div>
      )}

      {/* Ölçü ile otomatik m² hesabı (hesap tablosu açıkken gizlenir) */}
      {!(calcDef && showCalc) && (
      <div>
        <Label className="text-sm flex items-center gap-1.5">
          <Ruler className="w-3.5 h-3.5 text-teal-600" />
          {t('measureCalcLabel')}
        </Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          <FormattedNumberInput
            value={item.width_m || 0}
            onChange={(n) => handleDim("width_m", n)}
            placeholder={t('widthLabel')}
            className="rounded-lg bg-white"
            data-testid={`${itemType}-width-${index}`}
          />
          <FormattedNumberInput
            value={item.length_m || 0}
            onChange={(n) => handleDim("length_m", n)}
            placeholder={t('lengthLabel')}
            className="rounded-lg bg-white"
            data-testid={`${itemType}-length-${index}`}
          />
          <FormattedNumberInput
            value={item.height_m || 0}
            onChange={(n) => handleDim("height_m", n)}
            placeholder={t('heightLabel')}
            className="rounded-lg bg-white"
            data-testid={`${itemType}-height-${index}`}
          />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-0.5">
          <span className="text-[10px] text-slate-400 text-center">{t('widthLabel')}</span>
          <span className="text-[10px] text-slate-400 text-center">{t('lengthLabel')}</span>
          <span className="text-[10px] text-slate-400 text-center">{t('heightOptLabel')}</span>
        </div>
        {calc && (
          <p
            className="text-xs text-teal-700 font-medium mt-1"
            data-testid={`${itemType}-area-hint-${index}`}
          >
            {t(calc.labelKey)} = <b>{formatNumber(calc.area)} m²</b> → {t('autoTransferred')}
          </p>
        )}
      </div>
      )}

      {/* Fiyat Al */}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={fetchAiPrice}
          disabled={aiLoading}
          className="rounded-lg border-violet-300 text-violet-700 hover:bg-violet-50"
          data-testid={`${itemType}-ai-price-${index}`}
        >
          <Sparkles className={`w-4 h-4 mr-1.5 ${aiLoading ? "animate-pulse" : ""}`} />
          {aiLoading ? t('gettingPrice') : t('getPriceBtn')}
        </Button>
        {aiResult && (
          <div
            className="mt-2 space-y-2 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2.5"
            data-testid={`${itemType}-ai-result-${index}`}
          >
            {/* Malzeme satırı */}
            {aiResult.material && aiResult.material.avg_price > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="font-semibold text-slate-700 min-w-[70px]">{t('material')}:</span>
                <span className="text-slate-600">
                  Min <b>{formatMoney(aiResult.material.min_price, lang)}</b>
                </span>
                <span className="text-violet-700">
                  {t('avgLabel')} <b>{formatMoney(aiResult.material.avg_price, lang)}</b>
                </span>
                <span className="text-slate-600">
                  Max <b>{formatMoney(aiResult.material.max_price, lang)}</b>
                </span>
                <span className="text-slate-400">/ {aiResult.unit}</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => applyPrice(aiResult.material.avg_price)}
                  className="h-6 px-2 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded-md"
                  data-testid={`${itemType}-ai-apply-material-${index}`}
                >
                  {t('applyMaterialBtn')}
                </Button>
              </div>
            )}
            {/* İşçilik satırı */}
            {aiResult.labor && aiResult.labor.avg_price > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="font-semibold text-slate-700 min-w-[70px]">{t('labor')}:</span>
                <span className="text-slate-600">
                  Min <b>{formatMoney(aiResult.labor.min_price, lang)}</b>
                </span>
                <span className="text-amber-700">
                  {t('avgLabel')} <b>{formatMoney(aiResult.labor.avg_price, lang)}</b>
                </span>
                <span className="text-slate-600">
                  Max <b>{formatMoney(aiResult.labor.max_price, lang)}</b>
                </span>
                <span className="text-slate-400">/ {aiResult.unit}</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => applyPrice(aiResult.labor.avg_price)}
                  className="h-6 px-2 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-md"
                  data-testid={`${itemType}-ai-apply-labor-${index}`}
                >
                  {t('applyLaborBtn')}
                </Button>
              </div>
            )}
            {/* Kısa not (varsa) */}
            {aiResult.note && (
              <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-violet-200">
                <span className="text-xs text-slate-500 italic">{aiResult.note}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemSmartFields;
